import type { PatientNavBarIconSize } from '@/shared/model';
import { usePatientNavBarStore } from '@/shared/model';
import PushPinIcon from '@mui/icons-material/PushPin';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import { ButtonBase, IconButton, Stack, Tooltip, useMediaQuery } from '@mui/material';

const SIZE_OPTIONS: { value: PatientNavBarIconSize; label: string; tooltip: string }[] = [
  { value: 'small', label: 'S', tooltip: 'Small icons — fits more links on a narrow screen' },
  { value: 'large', label: 'L', tooltip: 'Large icons — easier to see' }
];

/**
 * The bar's own controls, sharing the bar's first row with the patient's name and ID so that the
 * whole of the second row belongs to the links.
 */
const PatientNavBarControls = () => {
  const isPinned = usePatientNavBarStore(state => state.isPinned);
  const togglePinned = usePatientNavBarStore(state => state.togglePinned);
  const iconSize = usePatientNavBarStore(state => state.iconSize);
  const setIconSize = usePatientNavBarStore(state => state.setIconSize);
  // The bar is always pinned on a touch screen (see PatientNavBar), so the toggle would do nothing.
  const canHover = useMediaQuery('(hover: hover)');

  return (
    <Stack direction="row" spacing={0.25} sx={{ alignItems: 'center', flexShrink: 0 }}>
      {/*
        Two plain buttons rather than a ToggleButtonGroup: the group clones its direct children to
        inject the selection props, so a Tooltip wrapper around each button would swallow them.
      */}
      <Stack
        direction="row"
        role="group"
        aria-label="Link icon size"
        sx={{ borderRadius: 1, border: 1, borderColor: 'divider', overflow: 'hidden' }}
      >
        {SIZE_OPTIONS.map(option => {
          const isActive = iconSize === option.value;
          return (
            <Tooltip key={option.value} title={option.tooltip}>
              <ButtonBase
                onClick={() => setIconSize(option.value)}
                aria-label={option.tooltip}
                aria-pressed={isActive}
                sx={{
                  paddingX: 0.85,
                  paddingY: 0.15,
                  fontSize: 10,
                  fontWeight: 700,
                  lineHeight: 1.4,
                  fontFamily: 'inherit',
                  color: isActive ? 'primary.contrastText' : 'text.secondary',
                  backgroundColor: isActive ? 'primary.main' : 'transparent',
                  '&:hover': { backgroundColor: isActive ? 'primary.dark' : 'action.hover' }
                }}
              >
                {option.label}
              </ButtonBase>
            </Tooltip>
          );
        })}
      </Stack>

      {canHover && (
        <Tooltip title="Pin the patient navigation links to always show or turn off for auto-hide (best for smaller screens)">
          <IconButton
            size="small"
            onClick={togglePinned}
            aria-label={isPinned ? 'Unpin patient links' : 'Pin patient links'}
            aria-pressed={isPinned}
            sx={{ padding: 0.5, color: isPinned ? 'primary.main' : 'text.secondary' }}
          >
            {isPinned ? <PushPinIcon sx={{ fontSize: 17 }} /> : <PushPinOutlinedIcon sx={{ fontSize: 17 }} />}
          </IconButton>
        </Tooltip>
      )}
    </Stack>
  );
};

export default PatientNavBarControls;
